import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store'
import { TestRollbackButton } from '@/components/email/TestRollbackButton'
import { ultraadminApi, DashboardStatsResponse } from '@/services/api/ultraadmin'
import { Button, LoadingSpinner } from '@/components/common'
import { StatCard, QuickActionCard } from '@/components/ultraadmin'

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Guten Morgen'
  if (hour < 18) return 'Guten Tag'
  return 'Guten Abend'
}

export function UltraadminDashboardPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStatsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchStats = useCallback(async () => {
    try {
      const data = await ultraadminApi.getDashboardStats()
      setStats(data)
      setError(null)
    } catch (err: any) {
      setError(err?.detail || 'Daten konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStats()
    const interval = setInterval(fetchStats, 60000)
    return () => clearInterval(interval)
  }, [fetchStats])

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const displayName = user?.first_name || user?.last_name
    ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
    : user?.email ?? 'Unbekannt'

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      <TestRollbackButton />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {getGreeting()}, {displayName}
        </h1>
        <p className="mt-1 text-gray-500">
          Ultraadmin Dashboard – Systemübersicht
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded flex items-center justify-between">
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={fetchStats}>
            Erneut versuchen
          </Button>
        </div>
      )}

      <h2 className="text-xl font-semibold text-gray-800 mb-4">Vertretungen</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard
          label="Gefüllt total"
          value={stats?.substitutions_filled_total ?? 0}
          color="green"
        />
        <StatCard
          label="Gefüllt diesen Monat"
          value={stats?.substitutions_filled_this_month ?? 0}
          color="blue"
        />
        <StatCard
          label="Gefüllt heute"
          value={stats?.substitutions_filled_today ?? 0}
          color="orange"
        />
      </div>

      {(stats?.overdue_school_count ?? 0) > 0 && (
        <div className="mb-6 p-4 bg-amber-100 text-amber-900 rounded cursor-pointer" onClick={() => navigate('/ultraadmin/overdue')}>
          Schulen im Verzug: {stats?.overdue_school_count}
        </div>
      )}
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Systemübersicht</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <StatCard
          label="Anzahl Schulen"
          value={stats?.total_schools ?? 0}
          color="purple"
        />
        <StatCard
          label="Anzahl User"
          value={stats?.total_users ?? 0}
          color="indigo"
        />
      </div>

      <h2 className="text-xl font-semibold text-gray-800 mb-4">Schnellzugriff</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <QuickActionCard
          title="Superuser Management"
          description="Superadmin-Accounts erstellen, löschen und Rechte übertragen"
          onClick={() => navigate('/ultraadmin/superusers')}
        />
        <QuickActionCard
          title="Statistik"
          description="Übersicht aller Schulen und User mit Filtermöglichkeiten"
          onClick={() => navigate('/ultraadmin/statistics')}
        />
        <QuickActionCard
          title="Schulen verwalten"
          description="Schulen erstellen, bearbeiten und deaktivieren"
          onClick={() => navigate('/ultraadmin/schools')}
        />
        <QuickActionCard
          title="Schulen im Verzug"
          description="Offene überfällige Rechnungen"
          onClick={() => navigate('/ultraadmin/overdue')}
        />
      </div>
    </div>
  )
}
